import { Order, OrderFilter } from 'models/order';
import { formatDate } from 'utils/format';
import {
    ProductionOverviewCellInput,
    ProductionOverviewCount,
    ProductionOverviewCountMode,
    ProductionOverviewDayCount,
    ProductionOverviewPeriod,
    ProductionOverviewRowKey,
} from 'models/production-overview';

/** The custom range may span at most one month (DD-3800). */
export const MAX_CUSTOM_RANGE_DAYS = 31;

export const EMPTY_COUNT: ProductionOverviewCount = { orderCount: 0, actionCount: 0 };

/**
 * A half-typed MUI date field reports an Invalid Date object, which must be treated
 * as "not set" — an Invalid Date that reaches buildDayKeys hangs the tab (DD-3859).
 */
export const isValidDate = (date: Date | null): date is Date => date !== null && !Number.isNaN(date.getTime());

/** "YYYY-MM-DD" in the browser's local calendar — the same day the user picked. */
export const formatDayKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const dayOfMonth = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
};

export const addDays = (date: Date, dayCount: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + dayCount);
    return result;
};

export const getTodayKey = (): string => formatDayKey(new Date());

/** The period starts six days back: today sits just left of centre, with one more day ahead (DD-3880). */
export const DEFAULT_START_DAYS_BACK = 6;

export const getDefaultStartDate = (): Date => addDays(new Date(), -DEFAULT_START_DAYS_BACK);

/** A day after today: rows that only record what has already happened are empty there. */
export const isFutureDay = (dayKey: string): boolean => dayKey > getTodayKey();

/** Every day key from `fromDate` to `toDate`, both inclusive. */
export const buildDayKeys = (fromDate: Date, toDate: Date): string[] => {
    if (!isValidDate(fromDate) || !isValidDate(toDate)) return [];
    const lastDayKey = formatDayKey(toDate);
    const dayKeys: string[] = [];
    for (
        let cursor = new Date(fromDate);
        formatDayKey(cursor) <= lastDayKey && dayKeys.length < MAX_CUSTOM_RANGE_DAYS;
        cursor = addDays(cursor, 1)
    ) {
        dayKeys.push(formatDayKey(cursor));
    }
    return dayKeys;
};

const parseDayKey = (dayKey: string): Date => {
    const [year, month, dayOfMonth] = dayKey.split('-').map(Number);
    return new Date(year, month - 1, dayOfMonth);
};

export const isWeekendDay = (dayKey: string): boolean => {
    const weekday = parseDayKey(dayKey).getDay();
    return weekday === 0 || weekday === 6;
};

/** Mondays carry the thin line that separates the weeks. */
export const isMonday = (dayKey: string): boolean => parseDayKey(dayKey).getDay() === 1;

export const getWeekdayLabel = (dayKey: string): string =>
    new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(parseDayKey(dayKey));

/** The full date, formatted as the Production planning header formats it. */
export const getDayDateLabel = (dayKey: string): string => formatDate(parseDayKey(dayKey), 'date');

export const getDayOfMonthLabel = (dayKey: string): string => {
    const date = parseDayKey(dayKey);
    return `${date.getDate()}/${date.getMonth() + 1}`;
};

export type CustomRangeValidationError = 'RANGE_END_BEFORE_START' | 'RANGE_TOO_LONG' | null;

export const validateCustomRange = (fromDate: Date | null, toDate: Date | null): CustomRangeValidationError => {
    if (!isValidDate(fromDate) || !isValidDate(toDate)) return null;
    const from = parseDayKey(formatDayKey(fromDate));
    const to = parseDayKey(formatDayKey(toDate));
    if (to < from) return 'RANGE_END_BEFORE_START';
    const dayCount = Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    if (dayCount > MAX_CUSTOM_RANGE_DAYS) return 'RANGE_TOO_LONG';
    return null;
};

/** How far the arrows move: one whole 14- or 30-day period. */
export const getPeriodDayCount = (period: ProductionOverviewPeriod): number =>
    period === ProductionOverviewPeriod.THIRTY_DAYS ? 30 : 14;

export const resolveDateRange = (
    period: ProductionOverviewPeriod,
    startDate: Date,
    customFromDate: Date | null,
    customToDate: Date | null,
): { fromDate: Date; toDate: Date } | null => {
    if (period === ProductionOverviewPeriod.FOURTEEN_DAYS)
        return { fromDate: startDate, toDate: addDays(startDate, 13) };
    if (period === ProductionOverviewPeriod.THIRTY_DAYS) return { fromDate: startDate, toDate: addDays(startDate, 29) };
    if (!isValidDate(customFromDate) || !isValidDate(customToDate)) return null;
    if (validateCustomRange(customFromDate, customToDate)) return null;
    return { fromDate: customFromDate, toDate: customToDate };
};

export const pickCount = (count: ProductionOverviewCount | undefined, mode: ProductionOverviewCountMode): number => {
    if (!count) return 0;
    return mode === ProductionOverviewCountMode.ACTIONS ? count.actionCount : count.orderCount;
};

export const dayCountsToMap = (
    dayCounts: ProductionOverviewDayCount[] | undefined,
): Map<string, ProductionOverviewCount> => new Map((dayCounts || []).map((dayCount) => [dayCount.day, dayCount]));

export const addCounts = (
    first: ProductionOverviewCount,
    second: ProductionOverviewCount,
): ProductionOverviewCount => ({
    orderCount: first.orderCount + second.orderCount,
    actionCount: first.actionCount + second.actionCount,
});

/** Every calendar day cell, header included, carries this; the sticky label column does not slide. */
export const DAY_CELL_ATTRIBUTE = 'data-day-cell';

/** Today's column: a lighter shade of the plan overview's current-hour yellow (#fff9c4), DD-3880. */
export const TODAY_COLUMN_COLOR = '#fffde7';

export const WEEKEND_COLUMN_COLOR = '#fafafa';

/**
 * Heat shading relative to the highest visible count. It is a see-through tint of the
 * theme's primary blue laid over the column colour, so a count stays distinct from the
 * grey weekend columns and today's yellow column still shows through (DD-3880).
 */
export const getHeatColor = (count: number, maxCount: number): string => {
    if (count <= 0 || maxCount <= 0) return 'transparent';
    const ratio = count / maxCount;
    let alpha = 0.45;
    if (ratio <= 0.17) alpha = 0.08;
    else if (ratio <= 0.33) alpha = 0.14;
    else if (ratio <= 0.5) alpha = 0.2;
    else if (ratio <= 0.67) alpha = 0.28;
    else if (ratio <= 0.83) alpha = 0.36;
    return `rgba(27, 110, 183, ${alpha})`;
};

// --- Calendar row/group models the page builds from the query data ---

export type OverviewCalendarRow = {
    rowKey: ProductionOverviewRowKey;
    labelKey: string;
    countsByDay: Map<string, ProductionOverviewCount>;
    /** A right-now count drawn in today's column only (Inbox, Without deadline). */
    pointInTimeOnly?: boolean;
    /** Deadline row only: today's cell also shows how many orders are late. */
    lateToday?: ProductionOverviewCount;
    /** Deadline and delivery date look ahead; every other row is empty after today (DD-3880). */
    hasFutureDays?: boolean;
};

export type OverviewCalendarGroup = {
    groupKey: string;
    labelKey: string;
    rows: OverviewCalendarRow[];
    /** Inbox and Received show a per-day total on the group header; Production does not. */
    showDayTotals: boolean;
    /** Inbox only: a right-now count drawn on the header itself, in today's column. */
    pointInTimeHeaderCount?: ProductionOverviewCount;
    /** Inbox only: the header count links to the Workflow tab's Inbox list instead of the dialog. */
    headerHref?: string;
    /** Received only: one aggregate cell (RECEIVED_ALL + day) behind each header total. */
    groupTotalRowKey?: ProductionOverviewRowKey;
};

export const sumGroupCountsForDay = (group: OverviewCalendarGroup, dayKey: string): ProductionOverviewCount =>
    group.rows.reduce((total, row) => addCounts(total, row.countsByDay.get(dayKey) || EMPTY_COUNT), EMPTY_COUNT);

/** The number drawn on a group header for one day (the Inbox's right-now count sits in today's column). */
export const getGroupHeaderCount = (
    group: OverviewCalendarGroup,
    dayKey: string,
    mode: ProductionOverviewCountMode,
): number => {
    if (group.pointInTimeHeaderCount) {
        return dayKey === getTodayKey() ? pickCount(group.pointInTimeHeaderCount, mode) : 0;
    }
    return pickCount(sumGroupCountsForDay(group, dayKey), mode);
};

/** The number drawn in one row/day cell; right-now rows only have a number today. */
export const getRowCount = (row: OverviewCalendarRow, dayKey: string, mode: ProductionOverviewCountMode): number => {
    if (row.pointInTimeOnly && dayKey !== getTodayKey()) return 0;
    return pickCount(row.countsByDay.get(dayKey), mode);
};

/** The cell input whose click-through list holds exactly the orders behind one row/day cell. */
export const buildRowCellInput = (row: OverviewCalendarRow, dayKey: string): ProductionOverviewCellInput => {
    if (row.pointInTimeOnly) return { rowKey: row.rowKey };
    return { rowKey: row.rowKey, day: dayKey };
};

/** The highest count shown anywhere in the calendar, used as the heat-shading reference. */
export const getVisibleMaxCount = (
    groups: OverviewCalendarGroup[],
    days: string[],
    mode: ProductionOverviewCountMode,
): number => {
    let maxCount = 0;
    for (const group of groups) {
        if (group.pointInTimeHeaderCount) {
            maxCount = Math.max(maxCount, pickCount(group.pointInTimeHeaderCount, mode));
        }
        for (const day of days) {
            if (group.showDayTotals) maxCount = Math.max(maxCount, pickCount(sumGroupCountsForDay(group, day), mode));
            for (const row of group.rows) {
                maxCount = Math.max(maxCount, pickCount(row.countsByDay.get(day), mode));
            }
        }
    }
    return maxCount;
};

/**
 * The Workflow tab's order list, linked the way its step boxes link it: a step by id, the
 * Inbox and "No step" lists by name and filters only. A clinic picked on the overview
 * carries over as the list's clinic filter.
 */
export const buildWorkflowOrdersUrl = (name: string, stepId?: string, filters: OrderFilter = {}): string => {
    const params = new URLSearchParams();
    if (stepId) params.set('id', stepId);
    params.set('name', name);
    if (Object.keys(filters).length) params.set('filters', JSON.stringify(filters));
    return `/workflow/orders?${params.toString()}`;
};

// --- Click-through dialog filters (the list is complete, so they filter client-side) ---

export type OverviewOrdersFilter = {
    search: string;
    customerId: string;
    stepName: string;
};

export const EMPTY_ORDERS_FILTER: OverviewOrdersFilter = { search: '', customerId: '', stepName: '' };

const orderSearchText = (order: Order): string =>
    [
        order.orderId,
        order.internalOrderId,
        order.patient?.firstName,
        order.patient?.lastName,
        order.dentist?.fullName,
        order.customer?.name,
        order.deliveryDetails?.destination?.name,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

export const filterOverviewOrders = (orders: Order[], filter: OverviewOrdersFilter): Order[] => {
    const searchWords = filter.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return orders.filter((order) => {
        if (filter.customerId && order.customer?._id !== filter.customerId) return false;
        if (filter.stepName && (order.workFlow?.currentStep?.name || '') !== filter.stepName) return false;
        if (!searchWords.length) return true;
        const text = orderSearchText(order);
        return searchWords.every((word) => text.includes(word));
    });
};
